// 마법사 동료 클래스 - 마법 공격을 사용하는 원거리 동료 (Companion 상속)
class MageCompanion extends Companion {
  constructor(scene, player, config) {
    super(scene, player, config, "MAGE");

    // 힐 관련 속성
    this.healAmount = config.HEAL_AMOUNT;
    this.healCooltime = config.HEAL_COOLTIME;
    this.lastHealTime = 0;
  }

  // 애니메이션 인덱스 정의 (부모 클래스 메서드 오버라이드)
  getAnimationIndices() {
    return {
      idle: 36, // 대기 애니메이션
      move: 49, // 걷기 애니메이션
      aim: 63, // 조준 애니메이션
      attack: 61, // 공격 애니메이션
      hit: 34, // 피격 애니메이션
      death: 23, // 죽음 애니메이션
    };
  }

  // 업데이트 메서드 오버라이드 (힐 기능 추가)
  update(deltaTime, enemies, companions = [], camera = null) {
    // 한국어 주석: 살아있을 때만 힐 로직 실행
    if (this.isAlive) {
      // 쿨타임마다 모든 파티원을 자동으로 힐
      const currentTime = Date.now() / 1000;
      this.healAllAllies(currentTime);
    }

    // 한국어 주석: 죽음 애니메이션 재생을 위해 항상 부모 클래스의 update 호출
    super.update(deltaTime, enemies, companions, camera);
  }

  // Mage 무기와 장비 숨기기 (부모 클래스 메서드 오버라이드)
  hideWeapons() {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh) {
        const childName = child.name ? child.name.toLowerCase() : "";

        // 모든 지팡이와 마법 도구 숨기기
        if (
          childName.includes("wand") ||
          childName.includes("spellbook_open")
        ) {
          child.visible = false;
        }
      }
    });
  }

  // AI 상태 머신 - 마법사도 바바리안처럼 aimDistance가 10배
  updateAI(deltaTime, enemies) {
    // 강제 idle 상태면 AI 업데이트 하지 않음 (게임오버 시 앉기 애니메이션 유지)
    if (this.isForcedIdle) return;

    const playerDistance = this.getDistanceToPlayer();
    const nearestEnemy = this.findNearestEnemy(enemies);
    const enemyDistance = nearestEnemy
      ? this.getDistanceToEnemy(nearestEnemy)
      : Infinity;
    const aimDistance = this.attackDistance * 10; // 마법사도 10배!

    // 상태별 핸들러 실행
    const context = {
      nearestEnemy,
      enemyDistance,
      playerDistance,
      aimDistance,
      deltaTime,
    };
    const handlers = {
      FOLLOW: this.handleFollowState.bind(this),
      AIM: this.handleAimState.bind(this),
      ATTACK: this.handleAttackState.bind(this),
      IDLE: this.handleIdleState.bind(this),
    };

    const handler = handlers[this.currentState];
    if (handler) handler(context);
  }

  // AIM 상태 처리 - 마법사도 적에게 달려간다!
  handleAimState({ nearestEnemy, enemyDistance, aimDistance }) {
    if (!nearestEnemy || enemyDistance > aimDistance) {
      this.transitionToFollow();
    } else if (enemyDistance <= this.attackDistance) {
      this.transitionToAttack(nearestEnemy);
    } else {
      // 적을 향해 이동하면서 바라봄
      this.moveTowardsEnemy(nearestEnemy);
      this.playAnimation("run");
    }
  }

  // 적을 향해 이동 (마법사 전용)
  moveTowardsEnemy(enemy) {
    if (!enemy) return;

    const distance = this.getDistanceToEntity(enemy);

    // 적과의 최소 거리 유지 (겹침 방지)
    const minDistance =
      this.colliderRadius + (enemy.colliderRadius || 0.5) + 0.2;

    if (distance > minDistance) {
      // 방향 벡터 계산
      const direction = new THREE.Vector3()
        .subVectors(enemy.position, this.position)
        .normalize();
      // 적 방향으로 회전
      this.lookAtDirection(direction);

      // 새로운 위치 계산 (멤버 변수 사용)
      const moveVector = direction.multiplyScalar(this.moveSpeed);
      const newPosition = new THREE.Vector3()
        .copy(this.position)
        .add(moveVector);

      // 위치 업데이트
      this.updatePosition(newPosition);
    }
  }

  // 모든 파티원 힐 (쿨타임마다 자동 실행, 애니메이션 없음)
  healAllAllies(currentTime) {
    // 쿨다운 체크
    if (currentTime - this.lastHealTime < this.healCooltime) {
      return;
    }

    // 모든 아군 수집 (플레이어 + 동료들)
    const allAllies = [];

    if (this.player && this.player.isAlive && !this.player.isDying) {
      allAllies.push(this.player);
    }

    if (this.companions && this.companions.length > 0) {
      this.companions.forEach((companion) => {
        if (companion && companion.isAlive && !companion.isDying) {
          allAllies.push(companion);
        }
      });
    }

    // 체력이 최대가 아닌 아군만 필터링
    const injuredAllies = allAllies.filter(
      (ally) => ally.health < ally.maxHealth
    );

    if (injuredAllies.length === 0) {
      return;
    }

    // 모든 부상당한 파티원 힐
    injuredAllies.forEach((ally) => {
      ally.heal(this.healAmount);
    });

    // 힐 시간 업데이트
    this.lastHealTime = currentTime;
  }
}

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = MageCompanion;
} else {
  window.MageCompanion = MageCompanion;
}

// webpack 빌드를 위해 default export 추가
export default MageCompanion;
